import { Button } from "@remo-co/ui-core/src/components/Button";
import { Container } from "@remo-co/ui-core/src/components/Container";
import { FormHelperText } from "@remo-co/ui-core/src/components/FormHelperText";
import { FormTextInput } from "@remo-co/ui-core/src/components/FormTextInput";
import { InputAdornment } from "@remo-co/ui-core/src/components/InputAdornment";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import {
  ChangeEvent,
  FormEventHandler,
  SyntheticEvent,
  useContext,
  useEffect,
  useState,
} from "react";
import { Events, TRACKING_CONTEXT } from "modules/tracking";
import { CheckCircle } from "@remo-co/ui-core/src/icons/CheckCircle";
import { PASSWORD_DEFAULTS } from "modules/auth/constants";
import { TCAndPrivacyPolicyDisclaimer } from "modules/branding/TCAndPrivacyPolicyDisclaimer";
import { commonPassword } from "modules/auth/utils";
import useAuth from "modules/auth/hooks/useAuth";
import { useCheckEventStatus } from "modules/event/hooks/useCheckEventStatus";
import { useI18n } from "i18n";
import useNotificationActions from "modules/notification/hooks/useNotificationActions";
import usePasswordValidation from "modules/auth/hooks/usePasswordValidation";
import { IEvent } from "modules/event/types";
import { ISignUpParams } from "modules/auth/types";
import { LOCAL_USER_TIMEZONE } from "modules/dateTime/constants";
import useQueryParams from "modules/queryParams/hooks/useQueryParams";
import { useHistory } from "react-router";
import { checkIfEmailInvited } from "./utils";
import { getWebsite } from "./UserDetailsToRegister";
import { useStyles } from "./SignUp.styles";

interface Props {
  event: IEvent;
  email: string;
  initialName?: string;
  setEmail: (arg: string) => void;
  setIsSigningUp: (arg: boolean) => void;
}

const SignUp = ({
  event,
  initialName = "",
  email,
  setEmail,
  setIsSigningUp,
}: Props): JSX.Element => {
  const history = useHistory();
  const { t } = useI18n(["common", "eventLanding", "auth"]);
  const [firstNameField, setFirstNameField] = useState({
    error: false,
    value: initialName,
    message: "",
  });
  const [lastNameField, setLastNameField] = useState({
    error: false,
    value: "",
    message: "",
  });
  const [passwordField, setPasswordField] = useState({
    error: false,
    value: "",
    message: "",
  });
  const styles = useStyles();
  const { checkIfEventHappeningNow } = useCheckEventStatus();
  const isHappeningNow = checkIfEventHappeningNow(event);
  const { signUp } = useAuth();
  const { addErrorNotification } = useNotificationActions();
  const { website, name, ref } = useQueryParams();
  const { track } = useContext(TRACKING_CONTEXT);

  const [isInvited, setIsInvited] = useState(false);
  const onFailure = () => setIsSigningUp(false);
  const { validatePassword } = usePasswordValidation(onFailure);

  useEffect(() => {
    if (name) {
      setFirstNameField({ ...firstNameField, value: `${name}`, message: "" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (passwordField.value.length > PASSWORD_DEFAULTS.MAX_LENGTH) {
      setPasswordField({
        value: passwordField.value,
        error: true,
        message: t("auth:max.password.limit", {
          length: PASSWORD_DEFAULTS.MAX_LENGTH,
        }),
      });
    } else {
      setPasswordField({
        value: passwordField.value,
        error: false,
        message: "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passwordField.value]);

  const handleFormSubmit: FormEventHandler<HTMLFormElement> = async (
    e: SyntheticEvent<HTMLFormElement>,
  ) => {
    e.preventDefault();
    e.stopPropagation();

    setIsSigningUp(true);

    if (!firstNameField.value) {
      setFirstNameField({
        value: "",
        error: true,
        message: t("auth:enter.first.name"),
      });
    }

    if (!lastNameField.value) {
      setFirstNameField({
        value: "",
        error: true,
        message: t("auth:enter.last.name"),
      });
    }

    const { error, value, message } = validatePassword(passwordField.value);

    if (error) {
      setPasswordField({
        error,
        value,
        message,
      });

      return;
    }

    if (passwordField.value && commonPassword(passwordField.value)) {
      setPasswordField({
        value: passwordField.value,
        error: true,
        message: t("auth:weak.password"),
      });
      setIsSigningUp(false);

      return;
    }

    if (!firstNameField.value || !lastNameField.value || !passwordField.value) {
      addErrorNotification({
        message: t("eventLanding:enter.correct.details"),
      });
      setIsSigningUp(false);

      return;
    }

    const userInfo: ISignUpParams = {
      firstName: firstNameField.value,
      lastName: lastNameField.value,
      password: passwordField.value,
      email,
      ref: ref?.toString(),
      website: getWebsite(website),
      timezone: LOCAL_USER_TIMEZONE,
    };

    const result = await signUp(userInfo);

    if (!result) {
      setIsSigningUp(false);
    } else {
      track(Events.EVENT_REGISTER_SIGNUP, {
        event: event.id, // remove after complete migration to datagol
        eventId: event.id,
      });

      if (isHappeningNow) {
        history.push(`/e/${event.code}`);
      }
    }
  };

  const handleEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
  };

  const handlePasswordChange = (e: ChangeEvent<HTMLInputElement>) => {
    setPasswordField({ ...passwordField, value: e.target.value, message: "" });
  };

  const handleFirstNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    setFirstNameField({
      ...firstNameField,
      value: e.target.value,
      message: "",
    });
  };

  const handleLastNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    setLastNameField({ ...lastNameField, value: e.target.value, message: "" });
  };

  const handleEmailBlur = async () => {
    checkIfEmailInvited(email, event, setIsInvited);
  };

  let id;

  if (isHappeningNow) {
    id = "almost-there-modal_primary-cta-join-event-now";
  } else {
    id = "just-one-more-step-modal_primary-cta-register-event";
  }

  return (
    <>
      <Typography variant="h5" style={{ paddingBottom: "20px" }}>
        {event.isPrivate
          ? t("eventLanding:remo.account.absent")
          : t("eventLanding:create.remo.text")}
      </Typography>
      <form
        data-testid="signup-form"
        onSubmit={handleFormSubmit}
        className={styles.form}
      >
        <FormTextInput
          label={t("eventLanding:your.email")}
          fullWidth
          autoFocus
          InputLabelProps={{ shrink: true }}
          name="email"
          placeholder={t("eventLanding:your.email")}
          value={email}
          onChange={handleEmailChange}
          onBlur={handleEmailBlur}
          disabled
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {event.isPrivate && isInvited && (
                  <CheckCircle className={styles.greenCheck} />
                )}
              </InputAdornment>
            ),
            classes: {
              root: styles.textFieldInput,
            },
          }}
          classes={{ root: styles.textField }}
        />
        <FormTextInput
          label={t("first.name")}
          fullWidth
          InputLabelProps={{ shrink: true }}
          name="name"
          placeholder={t("first.name")}
          value={firstNameField.value}
          onChange={handleFirstNameChange}
          InputProps={{
            classes: { root: styles.textFieldInput },
          }}
          classes={{ root: styles.textField }}
        />
        {firstNameField.message && (
          <FormHelperText error id="name-text" className={styles.infoText}>
            {firstNameField.message}
          </FormHelperText>
        )}
        <FormTextInput
          label={t("last.name")}
          fullWidth
          InputLabelProps={{ shrink: true }}
          name="lastName"
          placeholder={t("last.name")}
          value={lastNameField.value}
          onChange={handleLastNameChange}
          InputProps={{
            classes: { root: styles.textFieldInput },
          }}
          classes={{ root: styles.textField }}
        />
        {lastNameField.message && (
          <FormHelperText error id="name-text" className={styles.infoText}>
            {lastNameField.message}
          </FormHelperText>
        )}
        <Container bottom={20}>
          <FormTextInput
            label={t("eventLanding:your.password")}
            fullWidth
            InputLabelProps={{ shrink: true }}
            name="password"
            placeholder={t("eventLanding:password.password")}
            onChange={handlePasswordChange}
            value={passwordField.value}
            type="password"
            InputProps={{
              classes: { root: styles.textFieldInput },
            }}
            classes={{ root: styles.textField }}
          />
          <FormHelperText className={styles.helpText}>
            {t("eventLanding:min.password.validate", {
              length: PASSWORD_DEFAULTS.MIN_LENGTH,
            })}
          </FormHelperText>
          {passwordField.message && (
            <FormHelperText
              error
              id="password-text"
              className={styles.infoText}
            >
              {passwordField.message}
            </FormHelperText>
          )}
        </Container>
        <Container bottom={30}>
          <TCAndPrivacyPolicyDisclaimer />
        </Container>
        <Button
          disabled={passwordField.error || !passwordField.value}
          fullWidth
          variant="secondary"
          color="blue"
          type="submit"
          data-testid="login-button"
          id={id}
          contentWrapperClassName={styles.buttonContent}
        >
          {isHappeningNow
            ? t("eventLanding:event.join")
            : t("eventLanding:register.event")}
        </Button>
        <FormHelperText className={styles.infoText}>
          {t("eventLanding:info.safe")}
        </FormHelperText>
      </form>
    </>
  );
};

export default SignUp;
